import React from "react";
import { ChatConfigData, MessageConfigFromPostConfig, PostConfigData } from "../Shared/Party/PartyConfigData";
import { Post, PostDisplayProps } from "./Post";
import { StateManagerContext } from "./StateManagerContext";
import { Carousel } from "./Carousel";
import { UIConfigContext } from "./UIConfigContext";
import { ActionButton, ActionHandler, CreateAction, EmptyActionData } from "./ActionButton";
import { FanboxStyling } from "./CSSUtilities";

export type PostSharePanelProps = {
    config : PostConfigData;
    titles : string[];
    bodyTexts : string[];
    images : string[];
    height : number;
    multiplier : number;
    boosts : number;
}

type PostSharePanelState = 'select' | 'display' | 'thankyou';

export const PostSharePanel = ({config,  height, multiplier, boosts, titles, bodyTexts, images} : PostSharePanelProps) : JSX.Element=> {

    const uiConfig = React.useContext(UIConfigContext);
    const [panelState, setPanelState] = React.useState<PostSharePanelState>('select');
    const titleIndex = React.useRef<number>(0);
    const bodyIndex = React.useRef<number>(0);
    const imageIndex = React.useRef<number>(0);

    const updateTitleIndex = (index : number) => {titleIndex.current = index;}
    const updateBodyIndex = (index : number) => {bodyIndex.current = index;}
    const updateImageIndex = (index : number) => {imageIndex.current = index;}


    const stateManager = React.useContext(StateManagerContext);

    const postDisplayProps : PostDisplayProps = {
        postHeight  : Math.min(500, height - 70),
        messageHeight : 0,
        userId : stateManager.user().fanbox.id,
    }

    const gotoPreview : ActionHandler= (_action) => {
        config.title = titles[titleIndex.current];
        const pe = config.elements[0];
        pe.primary.text = bodyTexts[bodyIndex.current];
        if(pe.secondary !== undefined)
            pe.secondary.imageSrc = images[imageIndex.current];
        setPanelState('display');
        return true;
    }

    const previewAction = CreateAction('preview', [{permission : 'signedIn'}], gotoPreview);

    const buttonStyling : FanboxStyling = {
        width : uiConfig.width * 0.5,
        height : 50,
        boundary : {
            margin : {
                quadType : 'margin',
                top : 20,
                left : uiConfig.width * 0.25,
                right : 0,
                bottom : 0
            }
        },
        color : {
            background : 'orange',
            foreground : 'white'
        },
        font : {
            size : uiConfig.fontBase,
            weight : 'bold'
        }
    }

    const doShare : ActionHandler = (_action) => {
        
        const [message, chatId] = MessageConfigFromPostConfig(config, multiplier, boosts, stateManager.activity().national,0, stateManager.user().fanbox.id);
        const chat : ChatConfigData = {
            itemId : chatId,
            chatType : 'comment',
            messages : [],
            threads : [],
            startTime : Date.now(),
            endTime : Date.now() + 1000000
        }
        
        stateManager.addPostUserMessage(message, chat);
        setPanelState('thankyou');
        return true;
    }

    const doShareAction  = CreateAction('doShare', [{permission : 'signedIn'}], doShare);


    return (
        <div style={{
            width : uiConfig.width + 'px',
            minWidth : uiConfig.width + 'px',
            maxWidth : uiConfig.width + 'px',
            height : height + 'px',
            minHeight : height + 'px',
            maxHeight : height + 'px',
            backgroundColor : uiConfig.contolBackground,
            display : 'flex',
            flexDirection : 'column'
        }}>
            {(panelState === 'select') && <span style={{ fontSize : (uiConfig.fontBase * 2) + 'px', fontWeight : 700, fontFamily : 'roboto', color : '#ffff54'}}>Select your post title</span>}
            {(panelState === 'select') && <Carousel width={uiConfig.width} height={70} display={1} scrollButtonWidth={20} idPrefix="PostSharePanelSelect" userControlled={true} panels={
                titles.map((title)=> { 
                    return <div style={{ width : uiConfig.width + 'px', maxWidth : uiConfig.width + 'px', minWidth : uiConfig.width + 'px', height : '70px', minHeight : '70px'}}><span style={{ fontSize : (uiConfig.fontBase * 1.5) + 'px', fontWeight : 400, fontFamily : 'roboto', color : 'white', margin : 'auto'}}>{title}</span></div>
                })
            } notifyOnClick={updateTitleIndex} /> }
            {(panelState === 'select') && <span style={{ fontSize : (uiConfig.fontBase * 2) + 'px', fontWeight : 700, fontFamily : 'roboto', color : '#ffff54'}}>Select your body text</span>}
            {(panelState === 'select') && <Carousel width={uiConfig.width} height={200} display={1} scrollButtonWidth={20} idPrefix="PostSharePanelSelect" userControlled={true} panels={
                bodyTexts.map((text)=> { 
                    return <div style={{ width : uiConfig.width + 'px', maxWidth : uiConfig.width + 'px', minWidth : uiConfig.width + 'px', height : '200px', minHeight : '200px'}}><span style={{ fontSize : (uiConfig.fontBase * 1.5) + 'px', fontWeight : 400, fontFamily : 'roboto', color : 'white', margin : 'auto'}}>{text}</span></div>
                })
            } notifyOnClick={updateBodyIndex} /> }
            {(panelState === 'select') && <span style={{ fontSize : (uiConfig.fontBase * 2) + 'px', fontWeight : 700, fontFamily : 'roboto', color : '#ffff54'}}>Select your image</span>}
            {(panelState === 'select') && <Carousel width={uiConfig.width} height={300} display={1} scrollButtonWidth={20} idPrefix="PostSharePanelSelect" userControlled={true} panels={
                images.map((src)=> { 
                    return <div style={{ width : uiConfig.width + 'px', maxWidth : uiConfig.width + 'px', minWidth : uiConfig.width + 'px', height : '300px', minHeight : '300px', backgroundImage : 'url(' + src + ')', backgroundPosition : 'center'}}></div>
                })
            } notifyOnClick={updateImageIndex} /> }
            {(panelState === 'select') && <ActionButton text={'Preview'}
                    action={previewAction}
                    actionData={EmptyActionData}
                    styling={buttonStyling}
                    prompt={'right'} />}
            {(panelState === 'display') && <Post config={config} display={postDisplayProps} />}
            {(panelState === 'display') && <ActionButton text={'Share'}
                    action={doShareAction}
                    actionData={EmptyActionData}
                    styling={buttonStyling}
                    prompt={'left'} />}
            {(panelState === 'thankyou') && <span style={{
                fontSize : uiConfig.fontBase * 1.5,
                fontWeight : 600,
                fontFamily : 'roboto',
                color : 'white'
                
            }} >Thank you for your share. In 15-30 seconds your post will appear with your other user messages and you can see how many likes it is getting as well as the comments being made.</span>}

        </div>
    );




}